import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { showApiError, clearAuthStorage } from "@/utils";

const usePostData = (url, initialRequestBody, options = {}) => {
  const [responseData, setResponseData] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const requestBodyRef = useRef(initialRequestBody);
  const navigate = useNavigate();
  const location = useLocation();

  const postData = async (newRequestBody = null) => {
    if (newRequestBody) {
      requestBodyRef.current = newRequestBody;
    }

    if (!requestBodyRef.current) return;

    setIsLoaded(false);
    setError(null);
    setResponseData([]);

    if (options.skip) {
      return;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        ...options.headers,
      };

      const response = await fetch(url, {
        method: "POST",
        headers,
        credentials: "include", // Include httpOnly cookies
        body: JSON.stringify(requestBodyRef.current),
      });

      // Check if the response status is not ok (4xx or 5xx)
      if (!response.ok) {
        if (response.status === 401) {
          console.warn("Unauthorized access - redirecting to login");
          clearAuthStorage();
          localStorage.setItem(
            "redirectAfterLogin",
            location.pathname + location.search,
          );
          navigate("/login");
          return;
        }

        const errorData = await response.json().catch(() => null);
        const errorMessage =
          errorData?.message || `HTTP error! Status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const response_json = await response.json();
      const data = response_json["data"];

      setResponseData(data);
      setIsLoaded(true);
    } catch (error) {
      console.error("Error posting data:", error);
      showApiError(error.message);
      setError(error);
    }
  };

  useEffect(
    () => {
      requestBodyRef.current = initialRequestBody;
      postData(initialRequestBody);
    },
    // eslint-disable-next-line
    [url, JSON.stringify(initialRequestBody), options.skip],
  );

  return [responseData, isLoaded, error, postData];
};

export default usePostData;
